import { FC, useEffect, useRef, useCallback } from 'react'
import { PROFIL_VARIABLE, sanitizeHtml } from 'shared/utils/sanitizeHtml';
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { NsKey } from 'enums/NsKey'
import { Button } from 'components/ui/Button'
import { GenericLoading, Sizes } from 'components/ui/Loader'
import { XMarkIcon } from 'components/icons/Icons'
import {
  Maximize2,
  Minimize2,
  ArrowLeft,
  Paperclip,
  Send,
  Plus,
  X
} from 'lucide-react'
import { LeadEmailConfig } from './useEmailComposer'
import { useLeadEmailComposer } from './useLeadEmailComposer'

export interface LeadEmailComposerProps {
  emailConfig: LeadEmailConfig
  documentTitle: string
  documentType: 'pdf' | 'image'
  fileExtension: string
  isFullscreen: boolean
  onClose: () => void
  onBack: () => void
  onToggleFullscreen: () => void
}

export const LeadEmailComposer: FC<LeadEmailComposerProps> = ({
  emailConfig,
  documentTitle,
  documentType,
  fileExtension,
  isFullscreen,
  onClose,
  onBack,
  onToggleFullscreen
}) => {
  const { t } = useTranslation(NsKey.Common)
  const templateContainerRef = useRef<HTMLDivElement>(null)

  const {
    emailPreview,
    recipients,
    newRecipient,
    showEmailInput,
    isLoading,
    isSending,
    error,
    templateHtmlWithInputs,
    setNewRecipient,
    handleVariableChange,
    handleRemoveRecipient,
    handleRecipientKeyDown,
    handleRecipientBlur,
    handleShowEmailInput,
    handleSendEmail,
    collectVariablesFromDOM
  } = useLeadEmailComposer({ emailConfig, onClose, onBack })

  // Handler pour envoyer l'email avec les variables lues depuis le DOM
  const onSendEmail = useCallback(() => {
    const variables = collectVariablesFromDOM(templateContainerRef.current)
    handleSendEmail(variables)
  }, [collectVariablesFromDOM, handleSendEmail])

  // Event delegation pour capturer les changements des contenteditable
  const handleTemplateInput = useCallback(
    (e: Event) => {
      const target = e.target as HTMLElement
      // Gérer les contenteditable
      if (
        target.classList.contains('email-variable-content') &&
        target.isContentEditable
      ) {
        const editor = target.closest('.email-variable-editor') as HTMLElement
        if (editor?.dataset.variableName) {
          handleVariableChange(editor.dataset.variableName, sanitizeHtml(target.innerHTML, PROFIL_VARIABLE))
        }
      }
    },
    [handleVariableChange]
  )

  // Attacher les event listeners au container
  useEffect(() => {
    const container = templateContainerRef.current
    if (!container) return

    container.addEventListener('input', handleTemplateInput)

    return () => {
      container.removeEventListener('input', handleTemplateInput)
    }
  }, [handleTemplateInput])

  const renderHeader = () => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={t('COMMON.ACTIONS.BACK')}
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900">
          {t('COMMON.LABELS.SEND_EMAIL')}
        </h2>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onToggleFullscreen}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={
            isFullscreen
              ? t('COMMON.ACTIONS.EXIT_FULLSCREEN')
              : t('COMMON.ACTIONS.FULLSCREEN')
          }
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4 text-gray-600" />
          ) : (
            <Maximize2 className="w-4 h-4 text-gray-600" />
          )}
        </button>

        <button
          onClick={isFullscreen ? onToggleFullscreen : onClose}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={t('COMMON.ACTIONS.CLOSE')}
        >
          <XMarkIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </div>
  )

  const renderRecipientsSection = () => (
    <div className="px-6 py-4 border-b border-gray-200 bg-white shrink-0">
      <div className="flex flex-col gap-3">
        {/* Destinataires */}
        <div className="flex items-start gap-3">
          <label className="text-sm font-medium text-gray-700 shrink-0 w-16 pt-1.5">
            {t('COMMON.LABELS.TO')} :
          </label>
          <div className="flex-1 flex flex-wrap items-center gap-2">
            {recipients.map((email) => (
              <div
                key={email}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-sm"
              >
                <span className="truncate max-w-[200px]">{email}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveRecipient(email)}
                  className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {showEmailInput ? (
              <input
                type="email"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={handleRecipientKeyDown}
                onBlur={handleRecipientBlur}
                placeholder={t('COMMON.PLACEHOLDERS.ADD_EMAIL')}
                autoFocus
                className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-normal text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            ) : (
              <button
                type="button"
                onClick={handleShowEmailInput}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                {t('COMMON.ACTIONS.ADD_RECIPIENT')}
              </button>
            )}
          </div>
        </div>

        {/* Objet */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 shrink-0 w-16">
            {t('COMMON.LABELS.SUBJECT')} :
          </label>
          <p className="text-sm text-gray-900">{emailPreview?.subject}</p>
        </div>

        {/* Pièce jointe */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 shrink-0 whitespace-nowrap">
            {t('COMMON.LABELS.ATTACHMENT')} :
          </label>
          <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-100 rounded-lg text-sm">
            <Paperclip className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-700 truncate max-w-[300px]">
              {emailPreview?.fileLabel ||
                emailConfig?.fileName ||
                `${documentTitle}.${fileExtension}`}
            </span>
            <span className="text-gray-400 text-xs">
              ({documentType === 'pdf' ? 'PDF' : 'Image'})
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTemplatePreview = () => (
    <div className="flex-1 overflow-auto bg-white">
      <div
        ref={templateContainerRef}
        className="email-template-content"
        dangerouslySetInnerHTML={{ __html: templateHtmlWithInputs }}
      />
    </div>
  )

  const renderContent = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      {renderRecipientsSection()}
      {renderTemplatePreview()}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-t border-red-200 shrink-0">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  )

  const renderFooter = () => (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
      <Button outline onClick={onBack}>
        {t('COMMON.ACTIONS.CANCEL')}
      </Button>
      <Button color="primary" onClick={onSendEmail} disabled={isSending}>
        {isSending ? (
          <GenericLoading size={Sizes.sm} isInline loading />
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            {t('COMMON.ACTIONS.SEND')}
          </>
        )}
      </Button>
    </div>
  )

  const renderLoadingState = () => (
    <div className="flex flex-col bg-white rounded-2xl overflow-hidden h-[80vh]">
      {renderHeader()}
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <GenericLoading size={Sizes.lg} loading />
          <p className="text-gray-600">
            {t('COMMON.LABELS.LOADING_EMAIL_TEMPLATE')}
          </p>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return renderLoadingState()
  }

  if (!emailPreview) {
    // Preview indisponible (erreur API / template email en échec) : afficher l'erreur
    // au lieu d'un dialog vide — l'utilisateur peut revenir au document.
    return (
      <div className="flex flex-col bg-white rounded-2xl overflow-hidden h-[80vh]">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 px-8 text-center">
            <p className="text-sm text-red-600">
              {error || 'Erreur lors de la récupération du template email'}
            </p>
            <Button outline onClick={onBack}>
              Retour au document
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const content = (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[9999] flex flex-col bg-white'
          : 'flex flex-col bg-white rounded-2xl overflow-hidden h-[80vh]'
      }
    >
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </div>
  )

  if (isFullscreen) {
    return (
      <>
        <div className="hidden" />
        {createPortal(content, document.body)}
      </>
    )
  }

  return content
}
